import { useState, type ReactNode } from 'react';
import { line, JOB_TYPE_UNITS_PATH, Size, t, workOrderPlannedPartsPath, type JobTypeUnitsAnswer, type PlannedPartRow } from '@bms2/shared';
import { Button, Dropdown, NumericInput, Select } from '../../components/controls';
import { RowControls, TileSectionAction, TileSectionHead } from '../../components/composites/Tile';
import { FormFrame } from '../../components/forms';
import { useItemPicker } from '../settings/api';
import { useAddNew } from './AddNewHost';
import type { PartWindowStart } from './WorkOrderPartWindow';
import { del, patch, post, useApi } from './api';
import { RefusalText } from './RefusalText';
import styles from './RecordPage.module.css';

/** What the planned parts read off the job's own page. */
export interface PlannedPartsFacts {
  id: string;
  workOrderNumber: string;
  status: string;
  editable: boolean;
  plannedParts?: PlannedPartRow[];
  materials: { itemId?: string; quantity: number }[];
}

/** 🔒 Piece 29: a dispatcher changes the planned parts while the job is Draft or Scheduled, before the van is loaded. */
const PLANNABLE = new Set(['Draft', 'Scheduled']);

/**
 * 🔒 The job's planned parts (piece 29, T390, T1710): what the job expects to
 * use, pre-filled by its type or typed by a dispatcher. A planned part is an
 * expectation and never a movement: it moves no stock and posts no cost.
 * Record Fitted opens the parts tile's own Add Part window on the planned part,
 * which asks where it came off and writes through the one path (decisions 0530,
 * 10320).
 */
export function WorkOrderPlannedParts({ document, apiBaseUrl = '', onChanged, onError, onFit }: { document: PlannedPartsFacts; apiBaseUrl?: string; onChanged: () => void; /** 🔒 Carries the refusal's whole answer beside its sentence, so the page draws the route as a link (T3533). */ onError: (message: string, body?: unknown) => void; onFit: (start: PartWindowStart) => void }) {
  const [adding, setAdding] = useState(false);
  /** The planned part whose pencil was pressed, edited in the same window the Add opens (decision 6461). */
  const [editing, setEditing] = useState<PlannedPartRow | null>(null);
  const [busy, setBusy] = useState(false);
  const parts = document.plannedParts ?? [];
  const plannable = document.editable && PLANNABLE.has(document.status);
  const fittedOf = (itemId: string) => document.materials.filter((row) => row.itemId === itemId).reduce((sum, row) => sum + row.quantity, 0);
  const press = async (write: () => Promise<{ ok: boolean; message?: string; body?: unknown }>) => {
    setBusy(true);
    const outcome = await write();
    setBusy(false);
    if (!outcome.ok) return onError(outcome.message ?? '', outcome.body);
    onChanged();
  };
  return (
    <section data-work-order-planned-parts={document.workOrderNumber}>
      {/* 🔒 A section inside a tile follows the Tile-row rule (decision 6461): Add stands in the section's head, and each row carries the pencil and the remove. */}
      <TileSectionHead title={t('workOrder.planned.title')} name="planned-parts">
        {plannable ? <span data-planned-part-add="true"><TileSectionAction label={t('workOrder.planned.add')} onAct={() => setAdding(true)} /></span> : null}
      </TileSectionHead>
      <p className={styles.hint}>{t('workOrder.planned.hint')}</p>
      {parts.length === 0 ? <p className={styles.hint} data-planned-parts-none="true">{t('workOrder.planned.none')}</p> : null}
      <ul className={styles.plainList}>
        {parts.map((part) => {
          const fitted = fittedOf(part.itemId);
          return (
            <li key={part.id} className={styles.partRow} data-planned-part={part.sku}>
              {line(part.sku, '·', part.itemName, '·', String(part.quantity), part.unitName, part.fromJobType ? `· ${t('workOrder.planned.fromType')}` : null, fitted > 0 ? `· ${fitted} ${t('workOrder.planned.fitted')}` : null)}
              {document.editable ? (
                <span data-planned-part-fit={part.id}>
                  {/* 🔒 The one path: the parts tile's own Add Part window, opened on the planned item, quantity and unit, asking where it came off (decision 10320). */}
                  <Button size={Size.Small} text={[t('workOrder.planned.fit')]} disabled={busy} actions={[() => onFit({ itemId: part.itemId, itemLabel: line(part.sku, '·', part.itemName), quantity: part.quantity, unitId: part.unitId })]} />
                </span>
              ) : null}
              {plannable ? (
                <span className={styles.partControls} data-planned-part-remove={part.id}>
                  <RowControls
                    button={null}
                    onEdit={busy ? null : () => setEditing(part)}
                    editLabel={t('workOrder.planned.edit')}
                    onDelete={busy ? null : () => void press(() => del(workOrderPlannedPartsPath(document.id, part.id), apiBaseUrl))}
                    deleteLabel={t('workOrder.planned.remove')}
                  />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {/* 🔨 Mounted only while open, so each opening starts blank (piece 13). */}
      {adding || editing ? (
        <PlannedPartWindow
          key={editing?.id ?? 'new'}
          workOrderId={document.id}
          apiBaseUrl={apiBaseUrl}
          replacing={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onAdded={() => { setAdding(false); setEditing(null); onChanged(); }}
        />
      ) : null}
    </section>
  );
}

/** The window a planned part is typed in, drawn through the Form frame (decision 3060) with its refusal inside (decision 3063). */
function PlannedPartWindow({ workOrderId, apiBaseUrl, replacing, onClose, onAdded }: { workOrderId: string; apiBaseUrl: string; replacing: PlannedPartRow | null; onClose: () => void; onAdded: () => void }) {
  const read = useApi<JobTypeUnitsAnswer>(JOB_TYPE_UNITS_PATH, apiBaseUrl);
  const units = read.state.kind === 'ready' ? read.state.data.units : [];
  const addNew = useAddNew(apiBaseUrl);
  const { itemSearch, unitIds } = useItemPicker(apiBaseUrl, 'purchase');
  const [item, setItem] = useState<{ id: string; label: string } | null>(replacing ? { id: replacing.itemId, label: line(replacing.sku, '·', replacing.itemName) } : null);
  const [quantity, setQuantity] = useState<number | null>(replacing ? replacing.quantity : 1);
  const [unitId, setUnitId] = useState(replacing?.unitId ?? '');
  const [refusal, setRefusal] = useState<ReactNode>(null);
  const family = units.find((unit) => unit.id === unitId)?.familyId ?? null;
  const pickItem = (id: string, label: string) => {
    setItem({ id, label });
    setUnitId(unitIds.current.get(id) ?? '');
  };
  const add = async () => {
    if (!item) return;
    const body = { itemId: item.id, quantity, unitId: unitId || null };
    // 🔒 A correction is one route that updates the part in place (decision 6461); an Add is the other.
    const outcome = replacing ? await patch(workOrderPlannedPartsPath(workOrderId, replacing.id), body, apiBaseUrl) : await post(workOrderPlannedPartsPath(workOrderId), body, apiBaseUrl);
    if (!outcome.ok) return setRefusal(<RefusalText message={outcome.message} body={outcome.body} />);
    onAdded();
  };
  return (
    <>
      <FormFrame
        host="window"
        open
        title={replacing ? t('workOrder.planned.editTitle') : t('workOrder.planned.addTitle')}
        dataForm={replacing ? 'edit-work-order-planned-part' : 'work-order-planned-part'}
        typed={item !== null}
        onCancel={onClose}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
        act={{ label: replacing ? t('modal.save') : t('workOrder.planned.add'), disabled: !item || !(quantity ?? 0), onAct: () => add() }}
      >
        <Dropdown size={Size.Medium}
          key={item?.id ?? 'none'}
          label={t('workOrder.material.item')}
          entityLabel={t('item.entityItem')}
          search={itemSearch}
          value={item?.label ?? ''}
          onSelect={(row) => pickItem(row.id, row.line1)}
          onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => pickItem(row.id, row.line1))}
          required
        />
        <NumericInput size={Size.Small} label={t('workOrder.material.quantity')} kind="number" value={quantity} onChange={setQuantity} name="planned-part-quantity" />
        <Select size={Size.Small}
          label={t('settings.jobTypes.unit')}
          value={unitId}
          onChange={setUnitId}
          placeholder={t('workOrder.new.noneYet')}
          options={(family ? units.filter((unit) => unit.familyId === family) : []).map((unit) => ({ value: unit.id, label: unit.unitName }))}
          dataAttribute={{ 'data-planned-part-unit': 'true' }}
        />
      </FormFrame>
      {addNew.host}
    </>
  );
}
